import "./ResizableReorderable.css"

import { Children, useEffect, useRef, useState } from "react"

import { getPageOffsets } from "web/services/browser"

import Page from "./Page"
import calculatePages from "./calculatePages"
import classNames from "classnames"

const RESIZING_CLASSES = ["border-2", "border-dashed", "border-neutral-2"]
const PLACEHOLDER_CLASSES = ["bg-neutral-8", "shadow-inner"]

function sizeInGrid(el) {
  const sizeFromStyle = (style) => {
    return Number.parseInt(style.match(/\d/)[0])
  }

  const computedStyle = window.getComputedStyle(el)

  return {
    rows: sizeFromStyle(computedStyle.gridRow),
    cols: sizeFromStyle(computedStyle.gridColumn),
  }
}

function sizeInGridFromDimensions(height, width, cellSizeY, cellSizeX) {
  const size = (size, cellSize) => {
    const wholeCells = Math.floor(size / cellSize)
    const remainder = size % cellSize
    const overflow = remainder > wholeCells * 10 ? 1 : 0

    return wholeCells + overflow
  }

  return {
    rows: size(height, cellSizeY),
    cols: size(width, cellSizeX),
  }
}

export default function ResizableReorderable({
  children,
  items,
  pageWidth = 4,
  pageHeight = 100,
  maxItemWidth,
  maxItemHeight = 2,
  rowHeightPx = 200,
  containerClasses = "gap-5",
  onResizeItem = () => {},
  onMoveItem = () => {},
  pageItemContainerClasses,
  disableDropzone,
  canDragResize,
  canEdit = true,
  showBottomBorder,
  isDraggable,
}) {
  const [[itemPages, childPages], setPages] = useState([
    [items],
    [Children.toArray(children)],
  ])

  const gridKey = useRef(0)
  const pageSetNode = useRef()

  const isResizing = useRef(false)
  const isMoving = useRef(false)

  const resizeHandleStartPos = useRef()
  const changingEl = useRef()
  const elevatedEl = useRef()
  const changingElStartSize = useRef()
  const placeholderEl = useRef()
  const movedToPosition = useRef()
  const cellSizeX = useRef()
  const cellSizeY = useRef()

  useEffect(() => {
    const [newItemPages, newChildPages] = calculatePages({
      container: pageSetNode.current,
      items,
      children,
      pageWidth,
      pageHeight,
    })

    setPages([newItemPages, newChildPages])
  }, [children, items, pageHeight, pageWidth])

  function resizePlaceholder({ rows, cols }) {
    const rowSpan = Math.min(rows, maxItemHeight)
    const colSpan = Math.min(cols, maxItemWidth || pageWidth)

    placeholderEl.current.style.gridRow = `span ${rowSpan} / span ${rowSpan}`
    placeholderEl.current.style.gridColumn = `span ${colSpan} / span ${colSpan}`
  }

  // Item must be temporarily elevated above overlays just before
  // drag starts or the overlay will appear in the dragged image
  // We also have to do this when resizing incase the resize handle
  // is behind the Help icon... not fun.
  function temporarilyElevateItem(el) {
    el.classList.add("z-50")
    elevatedEl.current = el
  }

  function removeTemporaryElevation() {
    if (elevatedEl.current) {
      elevatedEl.current.classList.remove("z-50")
      elevatedEl.current = null
    }
  }

  function styleElementForMove() {
    changingEl.current.classList.add("opacity-50")
  }

  function removeMovingElementSyltes() {
    changingEl.current.classList.remove("opacity-50")
  }

  function handleDragStart(e) {
    if (!canEdit) return

    const el = e.target
    disableDropzone && disableDropzone(true)

    isMoving.current = true
    changingEl.current = el

    temporarilyElevateItem(el)
    setTimeout(removeTemporaryElevation, 0)
    setTimeout(styleElementForMove, 0)
  }

  function handleDragEnd() {
    if (!canEdit) return

    removeMovingElementSyltes()
    disableDropzone && disableDropzone(false)

    if (changingEl.current && movedToPosition.current !== undefined) {
      const fromPos = Number.parseInt(changingEl.current.id)
      const toPos = movedToPosition.current

      if (fromPos !== toPos) {
        onMoveItem(fromPos, toPos)
      }
    }

    isMoving.current = false
  }

  function handleDragOver(e) {
    if (!canEdit) return

    const overEl = e.target.closest(".resizable-reorderable")

    if (isMoving.current) {
      if (changingEl.current === overEl) return

      const overElOffsets = getPageOffsets(overEl)
      const midX = overElOffsets.left + overElOffsets.width / 2

      const itemPos = Number.parseInt(changingEl.current.id)
      const overPos = Number.parseInt(overEl.id)

      if (e.pageX < midX) {
        if (itemPos !== overPos - 1) {
          overEl.before(changingEl.current)
          movedToPosition.current = overPos

          gridKey.current++
        }
      } else {
        if (itemPos !== overPos + 1) {
          overEl.after(changingEl.current)
          movedToPosition.current = overPos

          gridKey.current++
        }
      }
    }
  }

  function handleResizeHandleMouseDown(e) {
    e.preventDefault()
    e.stopPropagation()

    const parent = e.target.closest(".resizable-reorderable")
    const offsets = getPageOffsets(parent)

    const size = sizeInGrid(parent)
    cellSizeX.current = (offsets.width - (size.cols - 1) * 10) / size.cols
    cellSizeY.current = (offsets.height - (size.rows - 1) * 10) / size.rows

    resizeHandleStartPos.current = { x: e.pageX, y: e.pageY }
    changingElStartSize.current = {
      height: offsets.height,
      width: offsets.width,
    }

    parent.style.position = "absolute"
    parent.style.top = `${offsets.top}px`
    parent.style.left = `${offsets.left}px`
    parent.style.height = `${offsets.height}px`
    parent.style.width = `${offsets.width}px`

    const placeholder = document.createElement("div")
    placeholder.className = parent.className

    placeholder.classList.add(...PLACEHOLDER_CLASSES)
    parent.classList.add(...RESIZING_CLASSES)

    parent.parentElement.insertBefore(placeholder, parent)

    isResizing.current = true
    changingEl.current = parent
    placeholderEl.current = placeholder
  }

  function cancelResize(newSize) {
    if (!isResizing.current) return

    isResizing.current = false
    changingEl.current.style.position = null
    changingEl.current.style.height = null
    changingEl.current.style.width = null
    changingEl.current.classList.remove(...RESIZING_CLASSES)

    if (newSize) {
      changingEl.current.style.gridRow = `span ${newSize.rows} / span ${newSize.rows}`
      changingEl.current.style.gridColumn = `span ${newSize.cols} / span ${newSize.cols}`
    }

    placeholderEl.current.remove()
  }

  function endResizing() {
    const itemId = Number.parseInt(changingEl.current.id)

    const placeholderSize = sizeInGrid(placeholderEl.current)

    onResizeItem(itemId, placeholderSize.cols, placeholderSize.rows)

    cancelResize(placeholderSize)
  }

  function handleMouseUp() {
    if (isResizing.current) {
      endResizing()
    }

    removeTemporaryElevation()
  }

  function handleMouseMove(e) {
    if (isResizing.current) {
      const { height: startingHeight, width: startingWidth } =
        changingElStartSize.current

      const { x, y } = resizeHandleStartPos.current

      const newHeight = startingHeight + (e.pageY - y)
      const newWidth = startingWidth + (e.pageX - x)

      changingEl.current.style.height = `${newHeight}px`
      changingEl.current.style.width = `${newWidth}px`

      const sizeNow = sizeInGridFromDimensions(
        newHeight,
        newWidth,
        cellSizeY.current,
        cellSizeX.current
      )

      resizePlaceholder(sizeNow)
    }
  }

  function handleMouseLeave() {
    cancelResize()
  }

  const xlGridCols = {
    1: "xl:grid-cols-[repeat(1,minmax(200px,1fr))]",
    2: "xl:grid-cols-[repeat(2,minmax(200px,1fr))]",
    3: "xl:grid-cols-[repeat(3,minmax(200px,1fr))]",
    4: "xl:grid-cols-[repeat(4,minmax(200px,1fr))]",
  }

  const gridClasses = classNames(
    "grid-cols-1",
    xlGridCols[pageWidth],
    containerClasses,
    "mb-5"
  )

  return (
    // NOTE: the bottom padding (pb-6) avoids accidental mouseLeave while dragging
    <div className="pb-6" key={gridKey.current} ref={pageSetNode}>
      {itemPages.map((itemPage, index) => (
        <Page
          key={index}
          items={itemPage}
          gridClasses={gridClasses}
          rowHeightPx={rowHeightPx}
          onMouseUp={handleMouseUp}
          onMoveMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onResizeHandleMouseDown={handleResizeHandleMouseDown}
          itemContainerClasses={pageItemContainerClasses}
          canDragResize={canDragResize}
          showBottomBorder={showBottomBorder && index + 1 < itemPages.length}
          canEdit={canEdit}
          isDraggable={isDraggable}
        >
          {childPages[index]}
        </Page>
      ))}
    </div>
  )
}
